// src/admin/components/task/TaskTimelineSection.jsx
import React from 'react';
import { formatDateTime } from '../../../shared/utils/formatDate';
import formatDurationShort from '../../../shared/utils/formatDuration';
import { TASK_DURATION_BY_KEY, NOT_REPORTED } from '../../utils/taskDurations';

// This block used to show one figure called "Duration", which was `completed_at − created_at`
// rounded to whole hours — cycle time, under a name that did not say so, in a unit that printed
// a fifteen-minute visit as `+0h`. The three spans are now named and shown separately: how long
// the task waited, how long the visit took, and the whole thing end to end.
function span(from, to) {
  if (!from || !to) return null;
  const ms = to - from;
  return ms >= 0 ? ms : null;
}

function Figure({ toneClass, label, definition, ms }) {
  const formatted = formatDurationShort(ms);

  return (
    <div className="text-center">
      <div className={`${toneClass} small`} title={definition}>{label}</div>
      <div className="fw-semibold">
        {formatted === null
          ? <span className="text-muted" title={NOT_REPORTED}>—</span>
          : formatted}
      </div>
    </div>
  );
}

export default function TaskTimelineSection({ task }) {
  const assignedAt = new Date(task.created_at);
  const startedAt = task.started_at ? new Date(task.started_at) : null;
  const completedAt = task.completed_at ? new Date(task.completed_at) : null;

  const lead = TASK_DURATION_BY_KEY.lead;
  const onSite = TASK_DURATION_BY_KEY.on_site;
  const cycle = TASK_DURATION_BY_KEY.cycle;
  const work = TASK_DURATION_BY_KEY.work;
  const reportedWorkHours = task.reported_work_time_hours;

  return (
    <div className="bg-light rounded p-4 mb-4">
      <div className="row">
        <div className="col-md-4">
          <div className="text-muted small">Workflow Name</div>
          <div className="fw-semibold">{task.workflow.name}</div>
        </div>
        <div className="col-md-4">
          <div className="text-muted small">Task Status</div>
          <div className="fw-semibold">{task.completed_at ? 'Completed' : 'Open'}</div>
        </div>
        <div className="col-md-4">
          <div className="text-muted small">Assigner</div>
          <div className="fw-semibold">{task.assigned_by_email}</div>
        </div>
      </div>

      <hr />

      <div className="text-muted small mb-2">Task Timeline</div>
      <div className="d-flex justify-content-between flex-wrap gap-3">
        <div className="text-center">
          <div className="text-primary small">Assigned</div>
          <div className="fw-semibold">{formatDateTime(assignedAt)}</div>
        </div>
        {startedAt && (
          <div className="text-center">
            <div className="text-primary small">On site</div>
            <div className="fw-semibold">{formatDateTime(startedAt)}</div>
          </div>
        )}
        {completedAt && (
          <div className="text-center">
            <div className="text-success small">Completed</div>
            <div className="fw-semibold">{formatDateTime(completedAt)}</div>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between flex-wrap gap-3 mt-3">
        <Figure
          toneClass="text-warning"
          label={lead.label}
          definition={lead.definition}
          ms={span(assignedAt, startedAt)}
        />
        <Figure
          toneClass="text-warning"
          label={onSite.label}
          definition={onSite.definition}
          ms={span(startedAt, completedAt)}
        />
        <Figure
          toneClass="text-warning"
          label={cycle.label}
          definition={cycle.definition}
          ms={span(assignedAt, completedAt)}
        />
        {/* Not a span: this is what the crew typed in, which is why it is formatted from hours
            rather than from the timestamps, and why its definition says so. */}
        <Figure
          toneClass="text-warning"
          label={work.label}
          definition={work.definition}
          ms={Number.isFinite(reportedWorkHours) ? reportedWorkHours * 60 * 60 * 1000 : null}
        />
      </div>
    </div>
  );
}
